import { ReactNode } from 'react';

import { DialogTitle } from '@mui/material';

import {
  Button,
  DialogContent,
  Divider,
  IconButton,
  Icons,
  mergeSx,
  SxProps,
  ExternalLink,
  Typography,
  Stack,
  tokens,
} from '@cast/design-system';
import { Cluster } from '@cast/types';
import { openChat } from '@cast/utils';

import { TerraformSvg } from 'assets/svg';

import { ClustersTable } from './_components/ClustersTable';

import { FailedToLoad } from '@cast/shared-messages-ui';

import { Search, SearchProvider } from '@cast/shared-search-ui';

import { EXTERNAL_LINKS } from '@cast/constants';

type TitleProps = {
  children: ReactNode;
  onClose: () => void;
};

const Title = ({ children, onClose }: TitleProps) => {
  return (
    <>
      <Stack
        direction="row"
        alignItems="center"
        height={28}
        position="absolute"
        top={8}
        right={8}
      >
        <>
          <Button
            variant="text"
            component={ExternalLink}
            link={EXTERNAL_LINKS.castai_terraform}
            startIcon={<TerraformSvg />}
            noIcon
          >
            Terraform
          </Button>

          <Divider
            orientation="vertical"
            color={tokens.theme.border.structural.neutral}
            sx={{
              height: 22,
              mx: 8,
            }}
          />
        </>

        <IconButton variant="ghost" size="sm" onClick={openChat}>
          <Icons.Question />
        </IconButton>
        <IconButton
          variant="ghost"
          size="sm"
          onClick={onClose}
          testId="close-dialog"
        >
          <Icons.X />
        </IconButton>
      </Stack>
      <DialogTitle component="div" sx={{ py: 24 }}>
        <Typography variant="h5">{children}</Typography>
      </DialogTitle>
    </>
  );
};

type SelectClusterProps = {
  data: Cluster[];
  title: string;
  isLoading: boolean;
  hasError: boolean;
  refetchClusters: () => void;
  onClose: () => void;
  onSelect: (cluster: Cluster) => void;
  sx?: SxProps;
  bottomDivider?: boolean;
  disableAnywhereSelection?: boolean;
  disabledAnywhereTooltip?: ReactNode;
};

export const SelectCluster = ({
  data,
  title,
  isLoading,
  hasError,
  refetchClusters,
  onClose,
  onSelect,
  sx,
  bottomDivider = true,
  disableAnywhereSelection,
  disabledAnywhereTooltip,
}: SelectClusterProps) => {
  return (
    <SearchProvider data={data} isLoading={isLoading}>
      <Stack
        position="relative"
        sx={mergeSx({ pb: 64 }, sx)}
        data-testid="select-cluster-list"
      >
        <Title onClose={onClose}>{title}</Title>

        <DialogContent sx={{ pb: 16 }}>
          <Typography
            variant="L10M"
            color={tokens.theme.fg.structural.secondary}
            mb={16}
          >
            Select cluster:
          </Typography>
          <Search />
        </DialogContent>

        {hasError ? (
          <FailedToLoad
            title="Failed to load clusters"
            refresh={refetchClusters}
          />
        ) : (
          <>
            <Divider />
            <ClustersTable
              onSelect={onSelect}
              disableAnywhereSelection={disableAnywhereSelection}
              disabledAnywhereTooltip={disabledAnywhereTooltip}
            />
            {bottomDivider && <Divider />}
          </>
        )}
      </Stack>
    </SearchProvider>
  );
};
